import { ReactElement, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from 'reactstrap';
import { createFetchContext } from '../../../components/contexts/FetchContextFactory';
import DisplayError from '../../../components/Info/DisplayError';
import TableEmptyContent from '../../../components/Text/TableEmptyContent';
import { TranslationsKeys } from '../../../locales/translations';
import { getTeacherAttendanceForm } from '../api';
import { FormResponse } from '../types';

interface BaseLoaderProps {
  children: (props: {
    data: any;
    refresh: () => Promise<void>;
  }) => ReactElement;
}

const { FetchProvider, FetchConsumer, useFetch } = createFetchContext<
  undefined,
  FormResponse
>();

export function useBaseLoader() {
  const context = useFetch();
  return {
    data: context.data,
    loading: context.loading,
    error: context.error,
    refresh: context.refresh,
  };
}

export const BaseLoaderConsumer = FetchConsumer;

export function BaseLoader({ children }: BaseLoaderProps) {
  const { t } = useTranslation(TranslationsKeys.TEACHER_ATTENDANCE_LIST);
  const texts = useMemo(
    () => ({
      table: {
        notActivePeriod: {
          title: t('table.notActivePeriod.title'),
          subtitle: t('table.notActivePeriod.subtitle'),
        },
      },
    }),
    [t],
  );

  const request = useCallback(() => {
    return getTeacherAttendanceForm();
  }, []);

  return (
    <FetchProvider request={request} defaultQuery={undefined} fetchImmediately>
      <FetchConsumer>
        {({ data, loading, error, refresh }) => {
          if (error) {
            if (error.data.code === 'PERIOD_NOT_FOUND') {
              return (
                <Card className="p-4">
                  <TableEmptyContent
                    customClassName="d-flex flex-column align-items-center no-result"
                    textAlign={'center'}
                    icon={'information'}
                    title={texts.table.notActivePeriod.title}
                    subtitle={texts.table.notActivePeriod.subtitle}
                  />
                </Card>
              );
            } else {
              return (
                <DisplayError
                  insideCard
                  textBody={error.code}
                  retryAction={refresh}
                  loadingAction={loading}
                />
              );
            }
          }

          return children({
            data,
            refresh,
          });
        }}
      </FetchConsumer>
    </FetchProvider>
  );
}
